import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import { useLanguage } from '@/contexts/language-context';
import { scheduleAfterFirstPaint } from '@/lib/schedule-after-first-paint';

export const currencyOptions = [
  { code: 'INR', countryCode: 'IN', locale: 'en-IN', label: 'INR · ₹' },
  { code: 'USD', countryCode: 'US', locale: 'en-US', label: 'USD · $' },
  { code: 'AED', countryCode: 'AE', locale: 'en-AE', label: 'AED · د.إ' },
  { code: 'KWD', countryCode: 'KW', locale: 'en-KW', label: 'KWD · د.ك' },
] as const;

export type CurrencyCode = (typeof currencyOptions)[number]['code'];
export type MarketCountryCode = (typeof currencyOptions)[number]['countryCode'];
export type CurrencyPreference = CurrencyCode | 'AUTO';

const COUNTRY_CACHE_KEY = 'al-malaki-geo-country-v1';
const RATES_CACHE_KEY = 'al-malaki-fx-rates-v1';
const CURRENCY_PREFERENCE_KEY = 'al-malaki-currency-preference-v1';
const COUNTRY_REFRESH_MS = 6 * 60 * 60 * 1000;
const COUNTRY_MAX_STALE_MS = 7 * 24 * 60 * 60 * 1000;
const RATES_REFRESH_MS = 12 * 60 * 60 * 1000;
const RATES_MAX_STALE_MS = 7 * 24 * 60 * 60 * 1000;

type CountryCache = {
  countryCode: string;
  fetchedAt: number;
};

type RateCache = {
  rates: Partial<Record<CurrencyCode, number>>;
  fetchedAt: number;
  updatedAt: string;
}

function readCountryCache(): CountryCache | null {
  if (typeof window === 'undefined') return null;

  try {
    const stored = window.localStorage.getItem(COUNTRY_CACHE_KEY);
    if (!stored) return null;
    const parsed = JSON.parse(stored) as Partial<CountryCache>;
    if (
      typeof parsed.countryCode !== 'string' ||
      !/^[A-Z]{2}$/.test(parsed.countryCode) ||
      typeof parsed.fetchedAt !== 'number' ||
      Date.now() - parsed.fetchedAt > COUNTRY_MAX_STALE_MS
    ) {
      return null;
    }
    return parsed as CountryCache;
  } catch {
    return null;
  }
}

function readRateCache(): RateCache | null {
  if (typeof window === 'undefined') return null;

  try {
    const stored = window.localStorage.getItem(RATES_CACHE_KEY);
    if (!stored) return null;
    const parsed = JSON.parse(stored) as Partial<RateCache>;
    if (
      !parsed.rates ||
      typeof parsed.fetchedAt !== 'number' ||
      typeof parsed.updatedAt !== 'string' ||
      Date.now() - parsed.fetchedAt > RATES_MAX_STALE_MS
    ) {
      return null;
    }

    const rates: Partial<Record<CurrencyCode, number>> = { INR: 1 };
    for (const option of currencyOptions) {
      if (option.code === 'INR') continue;
      const rate = parsed.rates[option.code];
      if (typeof rate !== 'number' || !Number.isFinite(rate) || rate <= 0) return null;
      rates[option.code] = rate;
    }

    return { rates, fetchedAt: parsed.fetchedAt, updatedAt: parsed.updatedAt };
  } catch {
    return null;
  }
}

function readCurrencyPreference(): CurrencyPreference {
  if (typeof window === 'undefined') return 'AUTO';

  try {
    const stored = window.localStorage.getItem(CURRENCY_PREFERENCE_KEY);
    if (stored === 'AUTO' || currencyOptions.some((option) => option.code === stored)) {
      return stored as CurrencyPreference;
    }
  } catch {
    // Use automatic detection when local storage is unavailable.
  }
  return 'AUTO';
}

function saveCache(key: string, value: unknown) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Currency caching is optional; the live lookup still works without local storage.
  }
}

function currencyForCountry(countryCode: string): CurrencyCode {
  return currencyOptions.find((option) => option.countryCode === countryCode)?.code ?? 'INR';
}

export function formatCurrency(amount: number | string, code: string, language: 'en' | 'fr' | 'ar' = 'en') {
  const option = currencyOptions.find((item) => item.code === code);
  const locale =
    language === 'fr'
      ? 'fr-FR'
      : language === 'ar'
        ? ({ INR: 'ar-IN', USD: 'ar-US', AED: 'ar-AE', KWD: 'ar-KW' } as Record<string, string>)[code] ?? 'ar'
        : option?.locale ?? 'en-IN';
  const currency = option?.code ?? 'INR';

  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
  }).format(Number(amount));
}

interface CurrencyContextValue {
  currency: CurrencyCode;
  targetCurrency: CurrencyCode;
  detectedCurrency: CurrencyCode;
  currencyPreference: CurrencyPreference;
  detectedCountryCode: string;
  countryCode: MarketCountryCode;
  setCurrencyPreference: (preference: CurrencyPreference) => void;
  isLocationLoading: boolean;
  isRatesLoading: boolean;
  locationError: boolean;
  rateError: boolean;
  convertAmount: (amount: number | string, sourceCurrency?: string) => number | null;
  convertAmountToINR: (amount: number | string, sourceCurrency?: string) => number | null;
  formatPrice: (amount: number | string, sourceCurrency?: string) => string;
}

const CurrencyContext = createContext<CurrencyContextValue | undefined>(undefined);

export function CurrencyProvider({ children }: { children: ReactNode }) {
  const { language, t } = useLanguage();
  const [countryCache, setCountryCache] = useState(readCountryCache);
  const [ratesCache, setRatesCache] = useState(readRateCache);
  const [currencyPreference, setCurrencyPreferenceState] = useState(readCurrencyPreference);
  const [isLocationLoading, setIsLocationLoading] = useState(() => !readCountryCache());
  const [isRatesLoading, setIsRatesLoading] = useState(false);
  const [locationError, setLocationError] = useState(false);
  const [rateError, setRateError] = useState(false);
  const detectedCountryCode = countryCache?.countryCode ?? 'IN';
  const detectedCurrency = currencyForCountry(detectedCountryCode);
  const targetCurrency =
    currencyPreference === 'AUTO' ? detectedCurrency : currencyPreference;
  const hasTargetRate =
    targetCurrency === 'INR' || typeof ratesCache?.rates[targetCurrency] === 'number';
  const currency = hasTargetRate ? targetCurrency : 'INR';

  const setCurrencyPreference = useCallback((preference: CurrencyPreference) => {
    setCurrencyPreferenceState(preference);
    try {
      window.localStorage.setItem(CURRENCY_PREFERENCE_KEY, preference);
    } catch {
      // The selection still applies for this session if local storage is unavailable.
    }
  }, []);

  useEffect(() => {
    try {
      window.localStorage.removeItem('al-malaki-currency');
    } catch {
      // Ignore unavailable local storage.
    }
  }, []);

  useEffect(() => {
    const cached = readCountryCache();
    if (cached && Date.now() - cached.fetchedAt < COUNTRY_REFRESH_MS) {
      setIsLocationLoading(false);
      return;
    }

    let active = true;
    const controller = new AbortController();
    let timeout = 0;
    const cancelScheduledRequest = scheduleAfterFirstPaint(() => {
      timeout = window.setTimeout(() => controller.abort(), 6000);
      void fetch('https://api.country.is/', { signal: controller.signal })
        .then(async (response) => {
          if (!response.ok) throw new Error('Country lookup failed');
          return response.json() as Promise<{ country?: unknown }>;
        })
        .then((result) => {
          const detectedCountry =
            typeof result.country === 'string' ? result.country.toUpperCase() : '';
          if (!/^[A-Z]{2}$/.test(detectedCountry)) {
            throw new Error('Country lookup returned an invalid country');
          }

          const nextCache = { countryCode: detectedCountry, fetchedAt: Date.now() };
          saveCache(COUNTRY_CACHE_KEY, nextCache);
          if (active) {
            setCountryCache(nextCache);
            setLocationError(false);
          }
        })
        .catch(() => {
          if (active) setLocationError(true);
        })
        .finally(() => {
          window.clearTimeout(timeout);
          if (active) setIsLocationLoading(false);
        });
    });
    if (!cached) setIsLocationLoading(true);

    return () => {
      active = false;
      cancelScheduledRequest();
      controller.abort();
      window.clearTimeout(timeout);
    };
  }, []);

  useEffect(() => {
    if (targetCurrency === 'INR') {
      setIsRatesLoading(false);
      setRateError(false);
      return;
    }

    const cached = readRateCache();
    const currentCache =
      cached && (!ratesCache || cached.fetchedAt > ratesCache.fetchedAt) ? cached : ratesCache;
    if (cached && currentCache === cached) setRatesCache(cached);
    if (currentCache && Date.now() - currentCache.fetchedAt < RATES_REFRESH_MS) {
      setIsRatesLoading(false);
      setRateError(false);
      return;
    }

    let active = true;
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 7000);
    setIsRatesLoading(!currentCache);
    setRateError(false);

    void fetch('https://open.er-api.com/v6/latest/INR', { signal: controller.signal })
      .then(async (response) => {
        if (!response.ok) throw new Error('Exchange-rate lookup failed');
        return response.json() as Promise<{
          result?: unknown;
          base_code?: unknown;
          rates?: Record<string, unknown>;
          time_last_update_utc?: unknown;
        }>;
      })
      .then((result) => {
        if (result.result !== 'success' || result.base_code !== 'INR' || !result.rates) {
          throw new Error('Exchange-rate response was invalid');
        }

        const rates: Partial<Record<CurrencyCode, number>> = { INR: 1 };
        for (const option of currencyOptions) {
          if (option.code === 'INR') continue;
          const rate = result.rates[option.code];
          if (typeof rate !== 'number' || !Number.isFinite(rate) || rate <= 0) {
            throw new Error(`Exchange rate unavailable for ${option.code}`);
          }
          rates[option.code] = rate;
        }

        const nextCache: RateCache = {
          rates,
          fetchedAt: Date.now(),
          updatedAt:
            typeof result.time_last_update_utc === 'string'
              ? result.time_last_update_utc
              : new Date().toISOString(),
        };
        saveCache(RATES_CACHE_KEY, nextCache);
        if (active) {
          setRatesCache(nextCache);
          setRateError(false);
        }
      })
      .catch(() => {
        if (active) setRateError(true);
      })
      .finally(() => {
        window.clearTimeout(timeout);
        if (active) setIsRatesLoading(false);
      });

    return () => {
      active = false;
      controller.abort();
      window.clearTimeout(timeout);
    };
  }, [targetCurrency]);

  const convertAmount = useCallback(
    (amount: number | string, sourceCurrency = 'INR'): number | null => {
      const numericAmount = Number(amount);
      if (!Number.isFinite(numericAmount)) return null;
      if (sourceCurrency === currency) return numericAmount;

      const sourceRate =
        sourceCurrency === 'INR'
          ? 1
          : currencyOptions.some((option) => option.code === sourceCurrency)
            ? ratesCache?.rates[sourceCurrency as CurrencyCode]
            : undefined;
      const targetRate = currency === 'INR' ? 1 : ratesCache?.rates[currency];
      if (
        typeof sourceRate !== 'number' ||
        typeof targetRate !== 'number' ||
        sourceRate <= 0 ||
        targetRate <= 0
      ) {
        return null;
      }
      return (numericAmount / sourceRate) * targetRate;
    },
    [currency, ratesCache],
  );

  const convertAmountToINR = useCallback(
    (amount: number | string, sourceCurrency = 'INR'): number | null => {
      const numericAmount = Number(amount);
      if (!Number.isFinite(numericAmount)) return null;
      if (sourceCurrency === 'INR') return numericAmount;

      const sourceRate = currencyOptions.some((option) => option.code === sourceCurrency)
        ? ratesCache?.rates[sourceCurrency as CurrencyCode]
        : undefined;
      if (typeof sourceRate !== 'number' || sourceRate <= 0) return null;
      return numericAmount / sourceRate;
    },
    [ratesCache],
  );

  const formatPrice = useCallback(
    (amount: number | string, sourceCurrency = 'INR') => {
      const converted = convertAmount(amount, sourceCurrency);
      return converted === null ? t('Local price unavailable') : formatCurrency(converted, currency, language);
    },
    [convertAmount, currency, language, t],
  );

  return (
    <CurrencyContext.Provider
      value={{
        currency,
        targetCurrency,
        detectedCurrency,
        currencyPreference,
        detectedCountryCode,
        countryCode: 'IN',
        setCurrencyPreference,
        isLocationLoading,
        isRatesLoading,
        locationError,
        rateError,
        convertAmount,
        convertAmountToINR,
        formatPrice,
      }}
    >
      {children}
    </CurrencyContext.Provider>
  );
}

export function useCurrency() {
  const context = useContext(CurrencyContext);
  if (!context) throw new Error('useCurrency must be used within CurrencyProvider');
  return context;
}